import * as XLSX from 'xlsx';
import {
  AlternativePartRow,
  CaseCalculationAlternativePart,
  CaseCalculationPart,
  OriginalPartRow
} from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { Organization } from '../../../interfaces/Organization';
import {
  ImportAlternativePartsFileHeader,
  ImportOriginalPartsFileHeader
} from '../../../helpers/Constants';
import { TranslateFn } from '../../../types/translator';
import { emptyAlternativeDraft, emptyCalculationPartDraft } from '../data/factories';

const readFirstSheet = (file: File): Promise<XLSX.WorkSheet> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target!.result, { type: 'binary' });
        resolve(wb.Sheets[wb.SheetNames[0]]);
      } catch {
        reject();
      }
    };
    reader.onerror = () => reject();
    reader.readAsBinaryString(file);
  });

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();
const toNumber = (v: unknown): number => Number(text(v).replace(',', '.')) || 0;

const headerMatches = (
  header: unknown[],
  expected: string[],
  requiredCount = expected.length
): boolean =>
  expected.every((label, i) =>
    i < requiredCount ? header[i] === label : header[i] === undefined || header[i] === label
  );

const parseQuality = (v: unknown, qualities: IReferential[]): IReferential | null =>
  qualities.find((q) => (q.code ?? '').toUpperCase() === text(v).toUpperCase()) ?? null;

const importError = (t: TranslateFn, reason: string): Error =>
  new Error(
    `${t(
      'ERROR_IMPORT_PARTS_BY_XLSX_FILE',
      'Importul nu s-a putut efectua! Verificati fisierul!'
    )} ${reason}`.trim()
  );

export const parseOriginalPartsFile = async (
  file: File,
  t: TranslateFn,
  partQualityTypes: IReferential[]
): Promise<CaseCalculationPart[]> => {
  const fail = (reason: string) => importError(t, reason);
  const ws = await readFirstSheet(file).catch(() => {
    throw fail(t('ERROR_IMPORT_FILE_UNREADABLE', 'Fisierul nu a putut fi citit.'));
  });

  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 });
  const H = ImportOriginalPartsFileHeader;
  if (!rows.length || !headerMatches(rows[0], [H.H1, H.H2, H.H3, H.H4, H.H5])) {
    throw fail(t('ERROR_IMPORT_HEADER_MISMATCH', 'Antetul fisierului nu corespunde sablonului.'));
  }

  const data = XLSX.utils
    .sheet_to_json<OriginalPartRow>(ws, {
      header: ['code', 'name', 'price', 'quality', 'units'],
      defval: ''
    })
    .slice(1);
  if (!data.length) throw fail(t('ERROR_IMPORT_NO_ROWS', 'Fisierul nu contine piese.'));

  return data.map((item, index) => {
    const quality = parseQuality(item.quality, partQualityTypes);
    const rowLabel = `${t('ERROR_IMPORT_ROW', 'Randul')} ${index + 2}`;
    if (!text(item.code))
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_CODE', 'cod lipsa.')}`);
    if (!text(item.name))
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_NAME', 'denumire lipsa.')}`);
    if (!text(item.price))
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_PRICE', 'pret lipsa.')}`);
    if (!text(item.units))
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_UNITS', 'numar bucati lipsa.')}`);
    if (!quality) {
      throw fail(
        `${rowLabel}: ${t('ERROR_IMPORT_INVALID_QUALITY', 'calitate invalida (OE / AM).')}`
      );
    }
    const part: CaseCalculationPart = {
      ...emptyCalculationPartDraft(),
      name: text(item.name),
      guideNumber: text(item.code),
      originalPrice: toNumber(item.price),
      carPartCategory: quality,
      carPartCategoryId: quality.id,
      pieceNr: toNumber(item.units)
    };
    return part;
  });
};

export const parseAlternativePartsFile = async (
  file: File,
  partIdByCode: { [code: string]: number },
  suppliers: Organization[],
  t: TranslateFn,
  partQualityTypes: IReferential[]
): Promise<CaseCalculationAlternativePart[]> => {
  const fail = (reason: string) => importError(t, reason);
  const ws = await readFirstSheet(file).catch(() => {
    throw fail(t('ERROR_IMPORT_FILE_UNREADABLE', 'Fisierul nu a putut fi citit.'));
  });

  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 });
  const H = ImportAlternativePartsFileHeader;
  if (
    !rows.length ||
    !headerMatches(rows[0], [H.H1, H.H2, H.H3, H.H4, H.H5, H.H6, H.H7, H.H8, H.H9, H.H10, H.H11], 8)
  ) {
    throw fail(t('ERROR_IMPORT_HEADER_MISMATCH', 'Antetul fisierului nu corespunde sablonului.'));
  }

  const data = XLSX.utils
    .sheet_to_json<AlternativePartRow>(ws, {
      header: [
        'originalCode',
        'alternativeCode',
        'name',
        'price',
        'quality',
        'producer',
        'supplier',
        'delivery',
        'availableQuantity',
        'location',
        'remarks'
      ],
      defval: ''
    })
    .slice(1);
  if (!data.length) throw fail(t('ERROR_IMPORT_NO_ROWS', 'Fisierul nu contine piese.'));

  return data.map((item, index) => {
    const quality = parseQuality(item.quality, partQualityTypes);
    const originalGuideNumber = text(item.originalCode);
    const originalPartId = partIdByCode[originalGuideNumber];

    const supplier =
      suppliers.filter((s) => s.companyNumber && s.companyNumber === text(item.supplier))[0] ||
      null;
    const rowLabel = `${t('ERROR_IMPORT_ROW', 'Randul')} ${index + 2}`;
    if (!originalGuideNumber) {
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_ORIGINAL_CODE', 'cod original lipsa.')}`);
    }
    if (originalPartId == null) {
      throw fail(
        `${rowLabel}: ${t(
          'ERROR_IMPORT_ORIGINAL_NOT_FOUND',
          'codul original nu exista intre piesele originale.'
        )}`
      );
    }
    if (!text(item.alternativeCode)) {
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_ALT_CODE', 'cod alternativ lipsa.')}`);
    }
    if (!text(item.name))
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_NAME', 'denumire lipsa.')}`);
    if (!text(item.price))
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_PRICE', 'pret lipsa.')}`);
    if (!text(item.producer)) {
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_PRODUCER', 'producator lipsa.')}`);
    }
    if (!supplier) {
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_SUPPLIER_NOT_FOUND', 'furnizor negasit.')}`);
    }
    if (!text(item.delivery)) {
      throw fail(`${rowLabel}: ${t('ERROR_IMPORT_MISSING_DELIVERY', 'termen livrare lipsa.')}`);
    }
    if (!quality) {
      throw fail(
        `${rowLabel}: ${t('ERROR_IMPORT_INVALID_QUALITY', 'calitate invalida (OE / AM).')}`
      );
    }

    const part: CaseCalculationAlternativePart = {
      ...emptyAlternativeDraft(),
      originalPartId,
      name: text(item.name),
      alternativeGuideNumber: text(item.alternativeCode),
      alternativePrice: toNumber(item.price),
      alternativePartQuality: quality,
      supplier,
      deliveryTime: text(item.delivery),
      producer: text(item.producer),
      stockLocation: text(item.location),
      availableQuantity: text(item.availableQuantity) ? toNumber(item.availableQuantity) : 0,
      remarks: text(item.remarks)
    };
    return part;
  });
};
